import React, { useEffect, useMemo, useRef, useState } from "react";
import { SlidersHorizontal, X } from "lucide-react";
import { EventRow, upcoming, groupByMonth } from "../components/km/Culture";
import { EVENT_CATS } from "../data/events";
import EventSubmit from "../components/km/EventSubmit";

const LABEL = { all: "All", soon: "Next 3 Days" };
const chip = "km-eyebrow text-[11px] sm:text-[12px] border border-[#141414] rounded-full px-4 sm:px-[18px] min-h-[44px] transition-colors";

export default function CulturePage() {
  const [filter, setFilter] = useState("all");
  const [submitOpen, setSubmitOpen] = useState(false);
  const [stuck, setStuck] = useState(false);
  const [sheet, setSheet] = useState(false);
  const chipsRef = useRef(null);
  const eventsRef = useRef(null);
  useEffect(() => {
    const io = new IntersectionObserver(([en]) => { setStuck(!en.isIntersecting && en.boundingClientRect.top < 0); if (en.isIntersecting) setSheet(false); }, { rootMargin: "-76px 0px 0px 0px" });
    io.observe(chipsRef.current);
    return () => io.disconnect();
  }, []);
  const pick = (c, fromSheet) => {
    setFilter(c);
    if (!fromSheet) return;
    setSheet(false);
    const y = eventsRef.current.getBoundingClientRect().top + window.scrollY - 76 - 64;
    window.scrollTo({ top: y, behavior: "smooth" });
  };
  const chips = (fromSheet) => ["all", "soon", ...EVENT_CATS].map((c) => (
    <li key={c}>
      <button type="button" aria-pressed={filter === c} onClick={() => pick(c, fromSheet)} data-testid={`${fromSheet ? "sheet" : "culture"}-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        className={`${chip} ${filter === c ? "bg-[#141414] text-white" : "bg-transparent text-[#141414] hover:bg-[#EAF1EC]"}`}>{LABEL[c] || c}</button>
    </li>
  ));
  const groups = useMemo(() => groupByMonth(upcoming(filter)), [filter]);
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) return;
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("km-flash");
      setTimeout(() => el.classList.remove("km-flash"), 2600);
    }, 400);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    window.scrollTo(0, 0);
    const prev = document.title;
    document.title = "What’s Good in the Neighborhood | Dallas Culture Calendar | Kristin McIntyre";
    return () => { document.title = prev; };
  }, []);
  let n = 0;
  return (
    <main className="pt-[76px] md:pt-[84px]" data-testid="culture-page">
      <section className="max-w-[1040px] mx-auto px-6 md:px-8 py-12 sm:py-20" aria-labelledby="ks-heading">
        <p className="km-eyebrow text-[11px] sm:text-[12px] text-[#2C4A3B] km-fade">The Culture Calendar</p>
        <h1 id="ks-heading" className="km-serif text-[44px] sm:text-6xl lg:text-[80px] leading-none tracking-[-0.01em] mt-4 km-fade">What’s good in <em>the neighborhood?</em></h1>
        <p className="km-serif text-[19px] sm:text-[22px] leading-[1.5] text-[#5E5E5E] max-w-[620px] mt-5 km-fade">As a creative girly, I love finding cool local experiences. There’s always something to do in Dallas, and these are the ones worth getting dressed for.</p>

        <ul ref={chipsRef} className="flex flex-wrap gap-2.5 mt-8 sm:mt-10" aria-label="Filter events" data-testid="culture-filters">{chips(false)}</ul>

        <div ref={eventsRef} aria-live="polite" data-testid="culture-events">
          {groups.length ? groups.map((g) => (
            <div key={g.key}>
              <h3 className="km-eyebrow text-[12px] sm:text-[13px] mt-10 sm:mt-14 pb-3.5 border-b-2 border-[#141414]" data-testid="culture-month">{g.label}</h3>
              <ul>{g.items.map((e) => <EventRow key={e.title + e.start} e={e} i={n++} />)}</ul>
            </div>
          )) : <p className="km-serif italic text-[22px] text-[#5E5E5E] py-8" data-testid="culture-empty">{filter === "soon" ? "Nothing in the next few days. Tap All to see what’s coming up." : "Nothing on the calendar here yet. Check back soon."}</p>}
        </div>

        <div className="mt-12 sm:mt-16 bg-[#EAF1EC] px-5 py-7 sm:p-10 flex flex-wrap gap-6 items-center justify-between">
          <p className="km-serif text-[24px] sm:text-[28px] leading-[1.25] max-w-[560px]">Hosting something the neighborhood should know about? Send it my way.</p>
          <button type="button" onClick={() => setSubmitOpen(true)} className="km-btn-dark hover:!bg-[#2C4A3B]" data-testid="culture-submit-event">Submit an Event</button>
        </div>
        <p className="text-[11px] tracking-[0.06em] text-[#5E5E5E] mt-6">Dates per organizers. Plans change, so confirm before you go. Kristin McIntyre, REALTOR® · eXp Luxury</p>
      </section>
      <div className={`sm:hidden fixed inset-x-0 top-[76px] z-40 transition-all duration-300 ${stuck ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-3 pointer-events-none"}`} data-testid="culture-sticky-filter">
        <div className="bg-white/95 backdrop-blur-md border-b border-black/10 px-6 py-2.5 flex items-center justify-between">
          <span className="km-eyebrow text-[10px] text-[#5E5E5E] truncate">Showing: <span className="text-[#141414]">{filter === "all" ? "All events" : LABEL[filter] || filter}</span></span>
          <button type="button" onClick={() => setSheet(!sheet)} aria-expanded={sheet} className="km-eyebrow text-[10px] flex items-center gap-2 border border-[#141414] rounded-full px-4 min-h-[40px] shrink-0" data-testid="culture-sticky-filter-btn">
            {sheet ? <X size={14} strokeWidth={1.5} /> : <SlidersHorizontal size={14} strokeWidth={1.5} />} Filter
          </button>
        </div>
        <div className={`grid transition-all duration-300 bg-white shadow-[0_16px_30px_-20px_rgba(0,0,0,0.4)] ${sheet ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
          <div className="overflow-hidden"><ul className="flex flex-wrap gap-2.5 px-6 py-4" aria-label="Filter events" data-testid="culture-sheet-filters">{chips(true)}</ul></div>
        </div>
      </div>
      <EventSubmit open={submitOpen} onOpenChange={setSubmitOpen} />
    </main>
  );
}
